import {keepPreviousData, useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {PublicUser} from '../types/public-user'

interface UsersResponse {
  users: PublicUser[]
}

export function normalizeUserIds(userIds: readonly number[]): number[] {
  return [...new Set(userIds)].sort((a, b) => a - b)
}

export function usePublicUsers(userIds: readonly number[]) {
  const normalizedUserIds = normalizeUserIds(userIds)

  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- User visibility is viewer-scoped and must not be persisted; this Rails endpoint has no generated response schema.
  return useQuery({
    queryKey: ['copilot-session-users', normalizedUserIds],
    queryFn: async (): Promise<ReadonlyMap<number, PublicUser>> => {
      const params = new URLSearchParams({user_ids: normalizedUserIds.join(',')})
      const response = await verifiedFetchJSON(`/copilot/agent-sessions/users?${params.toString()}`)
      if (!response.ok) {
        throw new Error(`Failed to resolve session users: ${response.status}`)
      }

      const {users} = (await response.json()) as UsersResponse
      return new Map(users.map(user => [user.id, user]))
    },
    enabled: normalizedUserIds.length > 0,
    placeholderData: keepPreviousData,
  })
}
